import { getSupabase as getSupabaseNullable } from "../../lib/supabase";
import { logger } from "../../lib/logger";
import type { UserPreferences, OnboardingState } from "../../types";

function requireSupabase() {
  const client = getSupabaseNullable();
  if (!client) throw new Error("Supabase not initialized. Call initSupabase() first.");
  return client;
}

const DEFAULT_ONBOARDING: OnboardingState = {
  checklistDismissed: false,
  checklistItems: {},
  completedTours: [],
};

function mapRow(row: Record<string, unknown>): UserPreferences {
  return {
    userId: row.user_id as string,
    tenantId: row.tenant_id as string,
    locale: row.locale as string,
    onboarding: (row.onboarding as OnboardingState) ?? DEFAULT_ONBOARDING,
    dismissedBanners: (row.dismissed_banners as string[]) ?? [],
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export class UserPreferencesService {
  /**
   * Get the current user's preferences. RLS ensures only their row is returned.
   */
  static async get(): Promise<UserPreferences | null> {
    const supabase = requireSupabase();
    const { data, error } = await supabase
      .from("user_preferences")
      .select("*")
      .limit(1)
      .maybeSingle();

    if (error) {
      logger.error("[UserPreferencesService] get error:", error.message);
      return null;
    }
    if (!data) return null;
    return mapRow(data as Record<string, unknown>);
  }

  /**
   * Update one or more preference fields for the current user.
   */
  static async update(
    fields: Partial<Pick<UserPreferences, "locale" | "onboarding" | "dismissedBanners">>
  ): Promise<void> {
    const supabase = requireSupabase();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const payload: Record<string, unknown> = {};
    if (fields.locale !== undefined) payload.locale = fields.locale;
    if (fields.onboarding !== undefined) payload.onboarding = fields.onboarding;
    if (fields.dismissedBanners !== undefined) payload.dismissed_banners = fields.dismissedBanners;

    if (Object.keys(payload).length === 0) return;

    const { error } = await supabase
      .from("user_preferences")
      .update(payload)
      .eq("user_id", user.id);

    if (error) {
      logger.error("[UserPreferencesService] update error:", error.message);
    }
  }

  /**
   * Convenience: update only the onboarding state.
   */
  static async updateOnboarding(state: OnboardingState): Promise<void> {
    return UserPreferencesService.update({ onboarding: state });
  }
}
